import type { InfiniteTableRowInfo } from "@infinite-table/infinite-react";
import cn from "classnames";
import type { NewsRaw, RelatedSource } from "epsm-api-types/models";
import { nanoid } from "nanoid";
import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { formatDateToFullTime } from "@/lib/utils/formatDateToFullTime/formatDateToFullTime";
import sprites from "/src/shared/assets/sprite.svg";
import { convertSentimentToString } from "../../../lib/helpers/convertSentimentToString";
import { useGetRowDetailHeight } from "../../../lib/hooks/useGetRowDetailHeight";
import { SentimentString } from "../../../types";
import { RelatedSources } from "../RelatedSources/ui/RelatedSources";

type Props = {
	newsItem: InfiniteTableRowInfo<NewsRaw>;
	setRowDetailHeight: (v: number) => void;
	setExpandedRowDetailList: React.Dispatch<React.SetStateAction<number[]>>;
};

export const NewsText: React.FC<Props> = (props) => {
	const { newsItem, setRowDetailHeight, setExpandedRowDetailList } = props;

	const relatedSources = (newsItem.data?.related_sources ??
		[]) as RelatedSource[];
	useGetRowDetailHeight<RelatedSource>(relatedSources, setRowDetailHeight);

	const summaryParts = useMemo(() => {
		return newsItem.data?.summary?.split("\n").filter(Boolean) ?? [];
	}, [newsItem.data?.summary]);

	if (!newsItem?.data) return null;

	const {
		title,
		sector,
		significancy,
		publication_date,
		url,
		related_sources,
	} = newsItem.data;

	const time = formatDateToFullTime(publication_date);
	const sentiment = convertSentimentToString(newsItem.data?.sentiment);

	return (
		<div
			className="relevant h-full w-full flex gap-2 flex-col"
			data-testid="news-text"
		>
			<div className="flex gap-2 justify-between items-center">
				<div className="flex gap-2 fw font-bold">
					<p data-testid="event-date">{` ${time} `}</p>
					<h2 className="text-gold">{title}</h2>
				</div>

				<Button
					className="h-max p-1 shadow-none bg-transparent hover:bg-transparent cursor-pointer"
					onClick={() =>
						setExpandedRowDetailList((prev) =>
							prev?.filter((i) => i !== newsItem?.indexInAll),
						)
					}
				>
					<svg
						aria-label="close"
						role="img"
						fill="none"
						stroke="var(--light-gray)"
						className="w-3.5 h-3.5 group-hover:stroke-accent-foreground"
					>
						<use xlinkHref={`${sprites}#close`} />
					</svg>
				</Button>
			</div>

			<div className="flex gap-2 fw font-bold">
				<p className="italic text-light-gray whitespace-nowrap">
					{sector && `${sector}`}
				</p>

				<p
					className={cn({
						"text-success-light": sentiment === SentimentString.Positive,
						"text-error-light": sentiment === SentimentString.Negative,
						"text-gray": sentiment === SentimentString.Neutral,
					})}
				>
					{sentiment}
				</p>

				<p className="whitespace-nowrap">
					{significancy && `Weight ${significancy}/10`}
				</p>
			</div>

			{summaryParts?.map((summaryPart) => {
				return <p key={`${summaryPart}-${nanoid()}`}>{summaryPart}</p>;
			})}

			{url && (
				<div className="text-link font-bold flex gap-2">
					<a href={url} target="_blank" rel="noreferrer">
						Open in a new tab
					</a>
				</div>
			)}

			{!!related_sources?.length && (
				<div className="pb-2">
					<RelatedSources relatedSources={related_sources} />
				</div>
			)}
		</div>
	);
};
